/**
 * Pure helpers for the boss matrix — tier grouping/labels, difficulty display, crystal-value
 * formatting and requirement eligibility. Kept separate from the components so they can be unit
 * tested without rendering anything (same split as `checklistGrouping.ts`).
 */
import type { BossDto } from './reference';

/** WEEKLY leads (it's the primary driver of the matrix), then MONTHLY, then DAILY last/collapsed. */
export const TIER_ORDER = ['WEEKLY', 'MONTHLY', 'DAILY'] as const;
export type BossTier = (typeof TIER_ORDER)[number];

export const TIER_LABELS: Record<BossTier, string> = {
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  DAILY: 'Daily',
};

export function tierRank(tier: string): number {
  const index = TIER_ORDER.indexOf(tier as BossTier);
  return index >= 0 ? index : TIER_ORDER.length;
}

export interface BossTierGroup {
  tier: BossTier;
  label: string;
  bosses: BossDto[];
}

/**
 * The lowest `levelReq` across a boss's difficulties — 0 for a boss with no seeded difficulties
 * (there is no unlock requirement to sort on, so it sorts first within its tier alongside any
 * other such boss).
 */
function minLevelReq(boss: BossDto): number {
  if (boss.difficulties.length === 0) {
    return 0;
  }
  return Math.min(...boss.difficulties.map((d) => d.levelReq));
}

/** The lowest `forceReq` across a boss's difficulties, `null`/none treated as 0 (2026-08-16 sort spec). */
function minForceReq(boss: BossDto): number {
  if (boss.difficulties.length === 0) {
    return 0;
  }
  return Math.min(...boss.difficulties.map((d) => d.forceReq ?? 0));
}

/**
 * Ascending "when does this boss unlock" order: minimum `levelReq` across the boss's difficulties
 * first, then minimum `forceReq` (NULL/none as 0) as a tiebreak, then name — so a later-unlocked
 * boss always sorts below an earlier one, regardless of alphabetical order (2026-08-16 user
 * request).
 */
function compareByUnlock(a: BossDto, b: BossDto): number {
  const levelDiff = minLevelReq(a) - minLevelReq(b);
  if (levelDiff !== 0) {
    return levelDiff;
  }
  const forceDiff = minForceReq(a) - minForceReq(b);
  if (forceDiff !== 0) {
    return forceDiff;
  }
  return a.name.localeCompare(b.name);
}

/**
 * Bosses grouped by tier in `TIER_ORDER`, each group sorted ascending by unlock requirement
 * (`compareByUnlock`) so a later-unlocked boss always appears lower within its tier.
 */
export function groupBossesByTier(bosses: BossDto[]): BossTierGroup[] {
  const buckets = new Map<string, BossDto[]>();
  for (const boss of bosses) {
    const bucket = buckets.get(boss.tier);
    if (bucket) {
      bucket.push(boss);
    } else {
      buckets.set(boss.tier, [boss]);
    }
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => tierRank(a) - tierRank(b))
    .map(([tier, groupBosses]) => ({
      tier: tier as BossTier,
      label: TIER_LABELS[tier as BossTier] ?? tier,
      bosses: [...groupBosses].sort(compareByUnlock),
    }));
}

/**
 * Full difficulty name for the option labels and the select's own display (2026-08-16 user
 * request: abbreviations like "H 89M" read as noise once the multiplied values got longer —
 * "Hard 445M" is unambiguous everywhere the difficulty is shown). No abbreviated form is used
 * anywhere in the app anymore, so there is nothing space-critical left to preserve it for.
 */
const DIFFICULTY_FULL: Record<string, string> = {
  EASY: 'Easy',
  NORMAL: 'Normal',
  HARD: 'Hard',
  CHAOS: 'Chaos',
  EXTREME: 'Extreme',
  VARIABLE: 'Variable',
  STORY: 'Story',
};

export function fullDifficultyName(difficulty: string): string {
  return DIFFICULTY_FULL[difficulty] ?? difficulty.charAt(0) + difficulty.slice(1).toLowerCase();
}

/**
 * GMS: Heroic (Reboot-style) worlds sell crystals at 5x the non-Heroic base price seeded in
 * `boss_difficulty.crystal_value` (docs/research/07-crystal-values.md). This is the frontend's one
 * copy of that rule — mirrors `dev.hypermaple.bosses.CrystalEconomy.HEROIC_MULTIPLIER` on the
 * backend, which is the source of truth for what the server actually charges/pays; nothing here
 * should hardcode `5` a second time.
 */
export const HEROIC_CRYSTAL_MULTIPLIER = 5;

/** `5` for a `HEROIC` world, `1` for every other (or unknown/undefined) `worldType`. */
export function crystalMultiplierForWorldType(worldType: string | undefined): number {
  return worldType === 'HEROIC' ? HEROIC_CRYSTAL_MULTIPLIER : 1;
}

/** "520000000" -> "520M", "1040000000" -> "1.04B"; `null` (unpriced) -> "?". */
export function formatCrystalValue(value: number | null): string {
  if (value === null) {
    return '?';
  }
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(2).replace(/\.?0+$/, '')}B`;
  }
  if (value >= 1_000_000) {
    return `${Math.round(value / 1_000_000)}M`;
  }
  if (value >= 1_000) {
    return `${Math.round(value / 1_000)}K`;
  }
  return String(value);
}

/** `"Hard 89M"` — full difficulty name + compact crystal value. */
export function difficultyOptionLabel(difficulty: string, crystalValue: number | null): string {
  return `${fullDifficultyName(difficulty)} ${formatCrystalValue(crystalValue)}`;
}

export interface CharacterForce {
  level: number;
  arcaneForce: number | null;
  sacredForce: number | null;
}

/**
 * Why (if at all) a character falls short of a difficulty's requirements — `null` means eligible.
 * `forceType` of `"NONE"` (or absent) means the difficulty has no force requirement at all.
 */
export function ineligibilityReason(
  character: CharacterForce,
  difficulty: { levelReq: number; forceType: string; forceReq: number | null },
): string | null {
  if (character.level < difficulty.levelReq) {
    return `Requires level ${difficulty.levelReq} (currently ${character.level})`;
  }
  if (difficulty.forceReq != null && difficulty.forceType !== 'NONE') {
    const forceLabel = difficulty.forceType === 'ARCANE' ? 'Arcane Force' : 'Sacred Force';
    const current =
      difficulty.forceType === 'ARCANE' ? character.arcaneForce : character.sacredForce;
    if ((current ?? 0) < difficulty.forceReq) {
      return `Requires ${difficulty.forceReq} ${forceLabel} (currently ${current ?? 0})`;
    }
  }
  return null;
}
